import React, { useEffect } from "react";
import { Button, Form, InputNumber, message, Radio, Select, Space } from "antd";
import { useQuery, useQueryClient } from "react-query";
import { errorText, payrollApi } from "./payrollApi";
import { card, GAP } from "./layout";

const WEEKDAYS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const PayrollSettings = () => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [saving, setSaving] = React.useState(false);

  const { data } = useQuery("payroll-settings", payrollApi.settings);

  useEffect(() => {
    if (data) form.setFieldsValue(data);
  }, [data, form]);

  const save = async () => {
    const v = await form.validateFields();
    try {
      setSaving(true);
      await payrollApi.saveSettings(v);
      message.success("Rules saved. Regenerate a draft payroll to apply them.");
      queryClient.invalidateQueries("payroll-settings");
    } catch (err) {
      message.error(errorText(err, "Could not save rules"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ ...card, padding: GAP.xl, maxWidth: 680 }}>
      <Form form={form} layout="vertical">
        <Form.Item
          name="weekly_offs"
          label="Weekly off days"
          extra="School holidays are taken from the holiday calendar automatically."
        >
          <Select mode="multiple" options={WEEKDAYS} />
        </Form.Item>

        <Form.Item
          name="paid_leave_types"
          label="Approved absence that stays paid"
        >
          <Select
            mode="multiple"
            options={[
              { value: "leave", label: "Leave" },
              { value: "sick", label: "Sick leave" },
              { value: "permission", label: "Permission" },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="short_time_rule"
          label="Short time (worked less than the required hours)"
        >
          <Radio.Group>
            <Space direction="vertical" size={GAP.xs}>
              <Radio value="none">No deduction</Radio>
              <Radio value="half_day">Half a day's pay for each short day</Radio>
              <Radio value="n_to_one">
                A full day's pay for every N short days
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(a, b) => a.short_time_rule !== b.short_time_rule}
        >
          {({ getFieldValue }) =>
            getFieldValue("short_time_rule") === "n_to_one" ? (
              <Form.Item name="short_time_n" label="N (short days per day's pay)">
                <InputNumber min={1} />
              </Form.Item>
            ) : null
          }
        </Form.Item>

        <Form.Item
          name="ot_standard_minutes"
          label="Standard working day (minutes)"
          extra="Time worked beyond this counts as overtime for staff with overtime enabled."
        >
          <InputNumber min={1} />
        </Form.Item>

        <div style={{ marginTop: GAP.xl }}>
          <Button type="primary" loading={saving} onClick={save}>
            Save rules
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default PayrollSettings;
